import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import DashboardHeader from "@/components/DashboardHeader";
import BackButton from "@/components/BackButton";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  ArrowRight,
  Compass,
  Loader2,
  Plus,
  Sparkles,
  Trash2,
} from "lucide-react";
import { toast } from "sonner";
import {
  ageBucketClass,
  ageBucketLabel,
  deleteLearningPath,
  generationStatusClass,
  generationStatusLabel,
  listLearningPaths,
  sensitiveBucketLabel,
} from "@/lib/learningPaths";

export default function StudentLearnHub() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const reload = () =>
    listLearningPaths()
      .then(setRows)
      .catch(() => toast.error("Failed to load your learning paths"))
      .finally(() => setLoading(false));

  useEffect(() => {
    reload();
    // Auto-poll while any path is generating.
    const t = setInterval(() => {
      if (rows.some((r) => r.generation_status === "queued" || r.generation_status === "running")) {
        listLearningPaths().then(setRows).catch(() => {});
      }
    }, 4000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows.length]);

  const handleDelete = async () => {
    if (!confirmDelete) return;
    setDeleting(true);
    try {
      await deleteLearningPath(confirmDelete.id);
      toast.success("Path deleted.");
      setConfirmDelete(null);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-stone-50 text-stone-900"
      data-testid="student-learn-hub"
    >
      <DashboardHeader subtitle="Student · Self-paced learning" />
      <main className="mx-auto max-w-6xl px-6 py-8 space-y-6">
        <BackButton to="/student" label="Dashboard" testId="back-to-dashboard" />

        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 text-amber-700 mb-1">
              <Sparkles className="h-4 w-4" />
              <span className="text-xs font-semibold uppercase tracking-wide">
                Learn anything · at your pace
              </span>
            </div>
            <h1 className="text-3xl font-semibold tracking-tight">
              Your learning paths
            </h1>
            <p className="text-sm text-stone-600 mt-1 max-w-xl">
              Type any topic. We'll craft an age-appropriate mini-course with
              lessons, homework and a test — graded on the fly by AI so you
              can move as fast as you want.
            </p>
          </div>
          <Button
            className="bg-amber-600 hover:bg-amber-700 text-white"
            onClick={() => navigate("/student/learn/new")}
            data-testid="new-path-button"
          >
            <Plus className="mr-1 h-4 w-4" /> New learning path
          </Button>
        </div>

        {loading ? (
          <div className="py-12 text-center text-sm text-stone-500">
            <Loader2 className="mx-auto h-5 w-5 animate-spin" />
          </div>
        ) : rows.length === 0 ? (
          <Card className="border-stone-200 border-dashed">
            <CardContent
              className="py-14 text-center space-y-3"
              data-testid="empty-paths"
            >
              <Compass className="mx-auto h-8 w-8 text-stone-400" />
              <div className="text-sm text-stone-700 font-medium">
                No learning paths yet
              </div>
              <div className="text-xs text-stone-500 max-w-md mx-auto">
                Pick any topic you're curious about — from "Chess openings" to
                "How black holes work" — and we'll draft a mini-course for you.
              </div>
              <Button
                onClick={() => navigate("/student/learn/new")}
                className="bg-amber-600 hover:bg-amber-700 text-white"
                data-testid="empty-new-path-button"
              >
                <Plus className="mr-1 h-4 w-4" /> Start your first path
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {rows.map((r) => (
              <Card
                key={r.id}
                className="border-stone-200 overflow-hidden transition hover:border-amber-400 hover:shadow-sm"
                data-testid={`learning-path-card-${r.id}`}
              >
                <div className="h-2 bg-gradient-to-r from-amber-400 to-amber-600" />
                <CardContent className="py-4 space-y-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div
                        className="font-medium text-sm line-clamp-2"
                        data-testid={`learning-path-title-${r.id}`}
                      >
                        {r.title}
                      </div>
                      {r.subtitle && (
                        <div className="text-xs text-stone-500 line-clamp-2 mt-0.5">
                          {r.subtitle}
                        </div>
                      )}
                    </div>
                    <button
                      className="text-stone-400 hover:text-red-600 shrink-0 transition"
                      onClick={() => setConfirmDelete(r)}
                      data-testid={`delete-path-${r.id}`}
                      aria-label="Delete learning path"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>

                  <div className="flex flex-wrap items-center gap-1.5">
                    <Badge
                      variant="outline"
                      className={ageBucketClass(r.age_bucket)}
                      data-testid={`age-bucket-${r.id}`}
                    >
                      {ageBucketLabel(r.age_bucket)}
                    </Badge>
                    <Badge
                      variant="outline"
                      className={generationStatusClass(r.generation_status)}
                      data-testid={`gen-status-${r.id}`}
                    >
                      {generationStatusLabel(r.generation_status)}
                    </Badge>
                    <Badge
                      variant="outline"
                      className="bg-stone-50 text-stone-600 border-stone-200"
                    >
                      {r.chapter_count} chapters
                    </Badge>
                    {r.sensitive_bucket && (
                      <Badge
                        variant="outline"
                        className="bg-blue-50 text-blue-800 border-blue-200"
                      >
                        {sensitiveBucketLabel(r.sensitive_bucket)}
                      </Badge>
                    )}
                  </div>

                  {r.generation_status === "running" ||
                  r.generation_status === "queued" ? (
                    <div className="flex items-center gap-2 text-xs text-amber-700">
                      <Loader2 className="h-3 w-3 animate-spin" />
                      Drafting your chapters ({r.generation_progress}%)
                    </div>
                  ) : r.generation_status === "failed" ? (
                    <div className="text-xs text-red-700">
                      Generation failed — delete and try a different topic.
                    </div>
                  ) : (
                    <Link to={`/student/learn/${r.id}`}>
                      <Button
                        className="bg-stone-900 hover:bg-stone-800 text-white w-full"
                        data-testid={`enter-path-${r.id}`}
                      >
                        Continue learning{" "}
                        <ArrowRight className="ml-1 h-3.5 w-3.5" />
                      </Button>
                    </Link>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>

      <AlertDialog
        open={!!confirmDelete}
        onOpenChange={(o) => !o && setConfirmDelete(null)}
      >
        <AlertDialogContent data-testid="delete-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this learning path?</AlertDialogTitle>
            <AlertDialogDescription>
              "{confirmDelete?.title}" and any progress on it will be permanently
              removed. This can't be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel-button">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="delete-confirm-button"
            >
              {deleting && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
